import styled, { css } from 'styled-components'

import { StyledDomElement } from '../../App'
import { Box } from '../../shared_components/UI/Box'
import { GridPatternBackground, NFTRandomText } from './Vault/styles'

export const Container = styled.div``

type HomePageSectionProps = {
  zIndex?: string
  ref?: any
  pb?: string
  pt?: string
  pbMobile?: string
  ptMobile?: string
} & StyledDomElement

export const HomePageSection = styled.div<HomePageSectionProps>`
  position: relative;
  z-index: ${(props) => props.zIndex};
  ${({ pb }) => pb && `padding-bottom: ${pb}`};
  ${({ pt }) => pt && `padding-top: ${pt}`};

  @media screen and (max-width: 1000px) {
    ${({ pbMobile }) => pbMobile && `padding-bottom: ${pbMobile}`};
    ${({ ptMobile }) => ptMobile && `padding-top: ${ptMobile}`};
  }
`

export const ProductsContainer = styled(Box)`
  position: relative;
  z-index: 1;
  h4 {
    color: ${({ theme }) => theme.semantics.zap.surface.primary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 300;
    line-height: 25px; /* 125% */
    letter-spacing: 6px;
    text-transform: uppercase;
  }

  h5 {
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; /* 140% */
    letter-spacing: -0.8px;
  }

  p {
    color: ${({ theme }) => theme.semantics.zap.surface.primary};
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px; /* 175% */
    letter-spacing: -0.64px;

    @media screen and (max-width: 768px) {
      line-height: 22px;
    }
  }

  .hover {
    transition: 0.3s ease;
    border-radius: 16px;
    padding: 16px;
    &:hover {
      transform: translateY(-10px);
      cursor: pointer;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`

export const BuiltByBox = styled(Box)`
  z-index: 1;
  h4 {
    color: ${({ theme }) => theme.semantics.zap.surface.primary};
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 32px;
    font-style: normal;
    font-weight: 300;
    line-height: 25px; /* 78.125% */
    letter-spacing: 9.6px;
    text-transform: uppercase;
  }

  h5 {
    color: ${({ theme }) => theme.semantics.zap.surface.primary};
    text-align: center;
    leading-trim: both;
    text-edge: cap;
    font-variant-numeric: ordinal;
    font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
    font-family: 'Space Grotesk';
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px; /* 116.667% */
    letter-spacing: -0.96px;
  }
`

export const BuiltByContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(197px, 1fr));
  gap: 32px;
  width: 100%;
  padding-bottom: 100px;

  @media screen and (max-width: 768px) {
    display: flex;
  }
`

export const ProductSectionBackground = styled.div<{
  backgroundUrl: string
  height?: string
  mobileH?: string
  align: 'left' | 'right'
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  height: ${({ height }) => height ?? '668px'};
  background-image: url(${({ backgroundUrl }) => backgroundUrl});
  background-size: contain;
  background-position: ${({ align }) => align};
  background-repeat: no-repeat;

  @media screen and (max-width: 768px) {
    height: ${({ mobileH }) => mobileH ?? '573px'};
  }

  @media (min-width: 1600px) {
    background-size: contain;
    ${({ align }) =>
      align === 'left'
        ? css`
            ::before {
              content: '';
              position: absolute;
              left: 50%;
              transform: translateX(-50vw);
              top: 0;
              overflow: hidden;
              width: 100%;
              max-width: calc((100vw - 1400px));
              height: 100%;
              z-index: 1;
              background: linear-gradient(
                90deg,
                #04111b 50%,
                rgba(4, 17, 27, 0) 100%
              );
            }
          `
        : css`
            ::after {
              content: '';
              position: absolute;
              right: 50%;
              transform: translateX(50vw);
              top: 0;
              overflow: hidden;
              width: 100%;
              max-width: calc((100vw - 1400px));
              height: 100%;
              z-index: 1;
              background: linear-gradient(
                -90deg,
                #04111b 50%,
                rgba(4, 17, 27, 0) 100%
              );
            }
          `}
  }
`

export const ProductContainer = styled.div<{ pY: string }>`
  padding: ${({ pY }) => pY} 0;
  height: 668px;
  display: flex;
  align-items: center;

  @media screen and (max-width: 768px) {
    height: auto;
    padding: 360px 0 28px 0;
  }
`

export const ProductCardContainer = styled.div<{ align: 'left' | 'right' }>`
  max-width: 734px;
  ${({ align }) =>
    align === 'left' ? `margin-right: auto` : 'margin-left: auto'};

  position: relative;
  > img {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    right: 0;
    margin: 0 auto;

    @media screen and (max-width: 1100px) {
      display: none;
    }
  }
`

export const ProductCard = styled(Box)`
  padding: 46px 60px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(3, 11, 17, 0.9);
  backdrop-filter: blur(20px);

  position: relative;
  z-index: 1;

  > div {
    > h4 {
      color: ${({ theme }) => theme.semantics.zap.surface.primary};
      font-feature-settings: 'ss01' on;
      font-family: 'Space Grotesk';
      font-size: 20.941px;
      font-style: normal;
      font-weight: 300;
      line-height: 16.36px; /* 78.125% */
      letter-spacing: 6.282px;
      text-transform: uppercase;
    }

    > h5 {
      color: ${({ theme }) => theme.semantics.zap.surface.primary};
      font-feature-settings: 'ss01' on;
      font-family: 'Space Grotesk';
      font-size: 48px;
      font-style: normal;
      font-weight: 700;
      line-height: 50px; /* 104.167% */
    }

    > p {
      color: ${({ theme }) => theme.semantics.zap.surface.primary};
      leading-trim: both;
      text-edge: cap;
      font-feature-settings: 'dlig' on, 'ss01' on;
      font-family: 'Space Grotesk';
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 28px; /* 140% */
      letter-spacing: -0.8px;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 46px 24px;

    > div {
      > h4 {
        font-size: 16px;
        line-height: 26px;
      }
      > h5 {
        font-size: 30px;
        line-height: 35px;
      }
      > p {
        font-size: 14px;
        line-height: 22px;
      }
    }

    .box-stats {
      margin-top: 24px;
      gap: 20px;
    }
  }
`

export function MegadropPrizes() {
  return (
    <MegadropPrizesContainer dc afe g={6} fu aw>
      <MegadropLabel>Rewards worth over</MegadropLabel>
      <MegadropCountdownCont fu aw jc g={4} ac z={1}>
        <MegadropCountdownItemCont children="$" $fontWeight={300} />
        <MegadropCountdownItemCont children="5" />
        <MegadropCountdownItemCont children="0" />
        <MegadropCountdownItemCont children="0" />
        <Comma afe>,</Comma>
        <MegadropCountdownItemCont children="0" />
        <MegadropCountdownItemCont children="0" />
        <MegadropCountdownItemCont children="0" />
      </MegadropCountdownCont>

      <Box dc afe relative g={20} alignSelf="flex-end">
        <GridPatternBackground right={0} isOverflow />
        <div className="bottom-grif-gradient" />
      </Box>
      <Box dc afe relative g={20} alignSelf="flex-end">
        <NFTRandomText dc afe g={6}>
          <span>
            JASIUKLKDHKJL UYTQL 8513-15894-5645641 JHGTYUJEHKJHE SD'KLJD
          </span>
          <span>9085390678290</span>
          <span>5468714456 - 45648468 / 468477 654 </span>
          <span>9986586790</span>
        </NFTRandomText>
      </Box>
    </MegadropPrizesContainer>
  )
}

const MegadropPrizesContainer = styled(Box)`
  z-index: 10;
  position: relative;
  padding-top: 48px;
  margin-left: auto;

  ${NFTRandomText} {
    padding-right: 14px;
  }

  @media (max-width: 1000px) {
    width: 100%;
    padding: 0;
    ${NFTRandomText} {
      display: none;
    }
    ${GridPatternBackground} {
      display: none;
    }
  }
`

export const MegadropLabel = styled.div`
  color: var(--White, #fff);
  text-align: right;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 24px; /* 150% */
  letter-spacing: 4.8px;
  text-transform: uppercase;

  padding-right: 14px;
  @media screen and (max-width: 768px) {
    padding-right: 0;
    padding-bottom: 10px;
  }
`

export const MegadropCountdownCont = styled(Box)`
  @media screen and (min-width: 768px) {
    border: 1px solid rgba(255, 255, 255, 0.05);
    background: rgba(3, 11, 17, 0.01);
    backdrop-filter: blur(30.95237922668457px);
    padding: 20px 14px;
  }
`

const MegadropCountdownItemCont = styled(Box)<{
  $fontWeight?: number
}>`
  flex: unset;
  width: auto;
  border-radius: 4.083px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(40.83272171020508px);

  padding: 0 10px;

  color: #fff;
  leading-trim: both;
  text-edge: cap;
  font-feature-settings: 'dlig' on;
  font-family: 'Space Grotesk';
  font-size: 60px;
  font-style: normal;
  font-weight: ${({ $fontWeight }) => $fontWeight ?? 700};

  line-height: 78px;
  height: 78px;
  min-width: 57px;
  text-align: center;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 40px;
    min-width: 44px;
    width: 44px;
    height: 51px;
    line-height: 51px;
  }

  @media (max-width: 370px) {
    min-width: 34px;
    width: 34px;
  }
`

const Comma = styled(Box)`
  line-height: 78px;
  height: 78px;
  margin-bottom: -16px;

  color: #fff;
  text-align: center;
  leading-trim: both;
  text-edge: cap;
  text-shadow: 0px 0px 122.498px #fdfd0f;
  font-family: 'Space Grotesk';
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.32px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 19px;
    line-height: 51px;
    height: 51px;
    margin-bottom: -32px;
  }
`

export const MegadropsHeroButtonBox = styled(Box)`
  display: none;

  @media screen and (min-width: 768px) and (max-width: 1200px) {
    display: flex;
  }
`

export const MegadropsHeroOverride = styled.div`
  @media screen and (max-width: 768px) {
    * {
      text-align: center;
      justify-content: center;
      align-items: center;
    }
  }
`

export const MegadropsHeroDescription = styled.div`
  h1 {
    font-family: Proxima Nova !important;
  }
  .disclaimer {
    margin-top: 8px;

    font-size: 14px;
    line-height: 1.6;
    opacity: 0.7;

    a {
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.brandPrimary};
    }
  }
`

export const BackdropZapPresents = styled.img`
  backdrop-filter: blur(16.11532974243164px);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
`
